import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Textarea } from "@/components/ui/textarea";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Save, Building2, Eye, Bell, Loader2, ChevronDown, Trash2, Plus, ShieldAlert, HeartPulse } from "lucide-react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { fetchJson, recomputeYear, errorMessage } from "./shared/configShared";
import { Help, TermLabel, Explained } from "./shared/Help";
import type { GlossaryKey } from "./shared/acaGlossary";

interface ACASettingsProps {
  clientId?: number;
}

// Exactly the writable columns of aca_settings.
interface SettingsForm {
  aleStartDate: string;
  employeeCountDay: "first_day" | "last_day" | "twelfth_day";
  isAggregatedAleGroup: boolean;
  aleOverride: boolean | null;
  aleOverrideReason: string;
  allowEmployeeViewForms: boolean;
  hideEmployee1095c: boolean;
  showHoursToEmployees: boolean;
  notifyDeadlines: boolean;
  notifyExceptions: boolean;
  reminderDays: number;
}

const toForm = (s: any): SettingsForm => ({
  aleStartDate: s?.aleStartDate ? String(s.aleStartDate).slice(0, 10) : "",
  employeeCountDay: (s?.employeeCountDay as SettingsForm["employeeCountDay"]) || "first_day",
  isAggregatedAleGroup: !!s?.isAggregatedAleGroup,
  aleOverride: s?.aleOverride ?? null,
  aleOverrideReason: s?.aleOverrideReason || "",
  allowEmployeeViewForms: s?.allowEmployeeViewForms !== false,
  hideEmployee1095c: !!s?.hideEmployee1095c,
  showHoursToEmployees: !!s?.showHoursToEmployees,
  notifyDeadlines: s?.notifyDeadlines !== false,
  notifyExceptions: s?.notifyExceptions !== false,
  reminderDays: s?.reminderDays ?? 30,
});

export default function ACASettings({ clientId: propClientId }: ACASettingsProps) {
  const { user } = useAuth();
  const clientId = propClientId ?? (user as any)?.clientId;
  const taxYear = new Date().getFullYear() - 1;
  const { toast } = useToast();
  const [form, setForm] = useState<SettingsForm>(toForm(null));
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [newMember, setNewMember] = useState({ memberName: "", memberEin: "", effectiveFrom: "", effectiveTo: "" });

  const { data: settings, isLoading } = useQuery<any>({
    queryKey: ["/api/aca/settings", clientId],
    queryFn: () => fetchJson(`/api/aca/settings?clientId=${clientId}`),
    enabled: !!clientId,
  });
  const { data: members = [] } = useQuery<any[]>({
    queryKey: ["/api/aca/ale-group-members", clientId],
    queryFn: () => fetchJson(`/api/aca/ale-group-members?clientId=${clientId}`),
    enabled: !!clientId,
  });
  const { data: dashboard } = useQuery<any>({
    queryKey: ["/api/aca/dashboard", clientId, taxYear],
    queryFn: () => fetchJson(`/api/aca/dashboard?clientId=${clientId}&taxYear=${taxYear}`),
    enabled: !!clientId,
  });

  const { data: yearData } = useQuery<any>({
    queryKey: ["/api/aca/year", clientId, taxYear],
    queryFn: () => fetchJson(`/api/aca/year?clientId=${clientId}&taxYear=${taxYear}`),
    enabled: !!clientId,
  });

  useEffect(() => { setForm(toForm(settings)); setOverrideOpen(settings?.aleOverride != null); }, [settings]);

  const update = <K extends keyof SettingsForm>(k: K, v: SettingsForm[K]) => setForm((f) => ({ ...f, [k]: v }));

  const saveMutation = useMutation({
    mutationFn: async () => {
      const body: any = {
        clientId,
        aleStartDate: form.aleStartDate || null,
        employeeCountDay: form.employeeCountDay,
        isAggregatedAleGroup: form.isAggregatedAleGroup,
        aleOverride: overrideOpen ? form.aleOverride : null,
        aleOverrideReason: overrideOpen && form.aleOverride != null ? form.aleOverrideReason : null,
        allowEmployeeViewForms: form.allowEmployeeViewForms,
        hideEmployee1095c: form.hideEmployee1095c,
        showHoursToEmployees: form.showHoursToEmployees,
        notifyDeadlines: form.notifyDeadlines,
        notifyExceptions: form.notifyExceptions,
        reminderDays: Number(form.reminderDays) || 0,
      };
      const res = await apiRequest("PUT", "/api/aca/settings", body);
      const saved = await res.json();
      await recomputeYear(clientId, taxYear);
      return saved;
    },
    onSuccess: () => {
      toast({ title: "Settings saved", description: "ACA results recalculated." });
      queryClient.invalidateQueries({ queryKey: ["/api/aca/settings"] });
    },
    onError: (e: any) => toast({ title: "Not saved", description: errorMessage(e), variant: "destructive" }),
  });

  const addMember = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/aca/ale-group-members", {
        clientId,
        memberName: newMember.memberName.trim(),
        memberEin: newMember.memberEin.trim() || null,
        effectiveFrom: newMember.effectiveFrom || null,
        effectiveTo: newMember.effectiveTo || null,
      });
      return res.json();
    },
    onSuccess: () => {
      setNewMember({ memberName: "", memberEin: "", effectiveFrom: "", effectiveTo: "" });
      queryClient.invalidateQueries({ queryKey: ["/api/aca/ale-group-members"] });
    },
    onError: (e: any) => toast({ title: "Could not add member", description: errorMessage(e), variant: "destructive" }),
  });
  const removeMember = useMutation({
    mutationFn: async (id: number) => apiRequest("DELETE", `/api/aca/ale-group-members/${id}?clientId=${clientId}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/aca/ale-group-members"] }),
    onError: (e: any) => toast({ title: "Could not remove member", description: errorMessage(e), variant: "destructive" }),
  });

  if (!clientId) return <Alert><AlertDescription>No client selected.</AlertDescription></Alert>;
  if (isLoading) return <div className="animate-pulse"><Card><CardContent className="h-64" /></Card></div>;

  const ale = dashboard?.ale;
  const planStartMonth: string | undefined = yearData?.result?.employees?.find((e: any) => e.planStartMonth && e.planStartMonth !== "00")?.planStartMonth;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">ACA Settings</h2>
          <p className="text-sm text-muted-foreground">Employer facts, audited overrides and preferences</p>
        </div>
        <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
          {saveMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Save Settings
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Building2 className="h-4 w-4" /> <TermLabel term="ale">Applicable Large Employer</TermLabel></CardTitle>
            <CardDescription>ALE status is calculated from the prior calendar year's monthly workforce. Override only as an audited exception.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="rounded-md border p-3 text-sm space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground"><TermLabel term="aleBasisYear">Calculated for {dashboard?.taxYear ?? taxYear}</TermLabel></span>
                {ale ? (
                  <span className="flex items-center gap-2">
                    <Explained term="ale"><Badge variant={ale.isAle ? "default" : "secondary"}>{ale.isAle ? "ALE" : "Not an ALE"}</Badge></Explained>
                    {ale.source === "override" && <Explained term="aleOverride"><Badge variant="outline">Override</Badge></Explained>}
                    {ale.estimated && ale.source !== "override" && <Explained term="aleEstimated"><Badge variant="outline">Estimated</Badge></Explained>}
                  </span>
                ) : <span>—</span>}
              </div>
              {ale && (
                <div className="text-muted-foreground">
                  Average {ale.averageFte} full-time + FTE across {ale.basisYear}{ale.source === "estimated_current" ? " (no prior-year data; estimated from the current workforce)" : ""}. <Help term="aleAverage" />
                </div>
              )}
            </div>

            <div className="space-y-1">
              <Label><TermLabel term="firstAleYear">First year as an ALE</TermLabel></Label>
              <Input type="date" value={form.aleStartDate} onChange={(e) => update("aleStartDate", e.target.value)} className="w-[220px]" />
              <p className="text-xs text-muted-foreground">Enables the January–March first-year Limited Non-Assessment Period for employees offered coverage by April 1.</p>
            </div>

            <div className="space-y-1">
              <Label><TermLabel term="employeeCountDay">1094-C total employee count day</TermLabel></Label>
              <Select value={form.employeeCountDay} onValueChange={(v) => update("employeeCountDay", v as SettingsForm["employeeCountDay"])}>
                <SelectTrigger className="w-[260px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="first_day">First day of each month</SelectItem>
                  <SelectItem value="last_day">Last day of each month</SelectItem>
                  <SelectItem value="twelfth_day">12th day of each month</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Permitted counting method for Part III column (c); must be applied consistently for the year.</p>
            </div>

            <Collapsible open={overrideOpen} onOpenChange={(o) => { setOverrideOpen(o); if (!o) update("aleOverride", null); }}>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm" className="px-0 text-muted-foreground">
                  <ChevronDown className={`h-4 w-4 mr-1 transition-transform ${overrideOpen ? "rotate-180" : ""}`} /> Advanced: override ALE status
                </Button>
              </CollapsibleTrigger>
              <Help term="aleOverride" className="ml-1" />
              <CollapsibleContent className="space-y-3 pt-2">
                <Alert>
                  <ShieldAlert className="h-4 w-4" />
                  <AlertDescription>Overrides are recorded with your user, timestamp and reason and appear in the History audit trail.</AlertDescription>
                </Alert>
                <div className="flex items-center justify-between">
                  <div>
                    <Label><TermLabel term="aleOverride">Override in effect</TermLabel></Label>
                    <p className="text-xs text-muted-foreground">Replaces the calculated status across Dashboard, Forms and History.</p>
                  </div>
                  <Switch checked={form.aleOverride != null} onCheckedChange={(v) => update("aleOverride", v ? true : null)} />
                </div>
                {form.aleOverride != null && (
                  <>
                    <div className="space-y-1">
                      <Label>Treat employer as</Label>
                      <Select value={form.aleOverride ? "yes" : "no"} onValueChange={(v) => update("aleOverride", v === "yes")}>
                        <SelectTrigger className="w-[220px]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="yes">Applicable Large Employer</SelectItem>
                          <SelectItem value="no">Not an ALE</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label><TermLabel term="override">Reason (required)</TermLabel></Label>
                      <Textarea value={form.aleOverrideReason} onChange={(e) => update("aleOverrideReason", e.target.value)} placeholder="e.g. Controlled group headcount confirmed by CPA; seasonal worker exception applies" />
                    </div>
                    {settings?.aleOverrideAt && (
                      <p className="text-xs text-muted-foreground">Last set by {settings.aleOverrideByUserId || "unknown"} on {new Date(settings.aleOverrideAt).toLocaleString()}.</p>
                    )}
                  </>
                )}
              </CollapsibleContent>
            </Collapsible>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Building2 className="h-4 w-4" /> <TermLabel term="aggregatedAleGroup">Aggregated ALE Group</TermLabel></CardTitle>
            <CardDescription>Related employers under common control (1094-C Part IV). Group-wide headcount decides ALE status.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label><TermLabel term="aggregatedAleGroup">Member of an aggregated ALE group</TermLabel></Label>
                <p className="text-xs text-muted-foreground">Controlled group or affiliated service group</p>
              </div>
              <Switch checked={form.isAggregatedAleGroup} onCheckedChange={(v) => update("isAggregatedAleGroup", v)} />
            </div>
            {form.isAggregatedAleGroup && (
              <div className="space-y-3">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Related employer</TableHead>
                      <TableHead>EIN</TableHead>
                      <TableHead>Membership</TableHead>
                      <TableHead className="w-10" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {members.length === 0 && (
                      <TableRow><TableCell colSpan={4} className="text-muted-foreground text-sm">No members recorded yet.</TableCell></TableRow>
                    )}
                    {members.map((m) => (
                      <TableRow key={m.id}>
                        <TableCell>{m.memberName}</TableCell>
                        <TableCell>{m.memberEin || "—"}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {m.effectiveFrom ? String(m.effectiveFrom).slice(0, 10) : "…"} → {m.effectiveTo ? String(m.effectiveTo).slice(0, 10) : "present"}
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="icon" onClick={() => removeMember.mutate(m.id)} disabled={removeMember.isPending}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2 items-end">
                  <div className="space-y-1 md:col-span-2">
                    <Label className="text-xs">Employer name</Label>
                    <Input value={newMember.memberName} onChange={(e) => setNewMember({ ...newMember, memberName: e.target.value })} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">EIN</Label>
                    <Input value={newMember.memberEin} onChange={(e) => setNewMember({ ...newMember, memberEin: e.target.value })} placeholder="12-3456789" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">From</Label>
                    <Input type="date" value={newMember.effectiveFrom} onChange={(e) => setNewMember({ ...newMember, effectiveFrom: e.target.value })} />
                  </div>
                  <Button size="sm" onClick={() => addMember.mutate()} disabled={!newMember.memberName.trim() || addMember.isPending}>
                    <Plus className="h-4 w-4 mr-1" /> Add
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><HeartPulse className="h-4 w-4" /> Derived from Benefits</CardTitle>
            <CardDescription>Read-only. Change these in the plan library or the employer's plan links.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground"><TermLabel term={dashboard?.selfInsured ? "selfInsured" : "fullyInsured"}>Health plan funding</TermLabel></span>
              <span className="font-medium">{dashboard ? <Explained term={dashboard.selfInsured ? "selfInsured" : "fullyInsured"}><span className="underline decoration-dotted">{dashboard.selfInsured ? "Self-insured (Part III required)" : "Fully insured"}</span></Explained> : "—"}</span>
            </div>
            {planStartMonth && (
              <div className="flex justify-between">
                <span className="text-muted-foreground"><TermLabel term="planStartMonth">Plan year start month</TermLabel></span>
                <span className="font-medium">{planStartMonth}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-muted-foreground"><TermLabel term={dashboard?.method === "look-back" ? "lookBack" : "monthlyMeasurement"}>Measurement method</TermLabel></span>
              <span className="font-medium">{dashboard?.method === "look-back" ? "Look-back" : "Monthly"} (Policies tab)</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Eye className="h-4 w-4" /> Employee Visibility</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Toggle term="employeeVisibility" label="Show ACA status to employees" hint="Employees see their own monthly status in the employee portal" checked={form.allowEmployeeViewForms} onChange={(v) => update("allowEmployeeViewForms", v)} />
            <Toggle term="form1095c" label="Hide 1095-C download" hint="Employees can view status but not download the form" checked={form.hideEmployee1095c} onChange={(v) => update("hideEmployee1095c", v)} />
            <Toggle term="hoursOfService" label="Show monthly hours" hint="Include hours of service in the employee view" checked={form.showHoursToEmployees} onChange={(v) => update("showHoursToEmployees", v)} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Bell className="h-4 w-4" /> Notifications</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Toggle term="deadlineFurnish" label="Filing and furnishing deadlines" hint="Reminders before each IRS deadline" checked={form.notifyDeadlines} onChange={(v) => update("notifyDeadlines", v)} />
            <Toggle term="exceptionWarning" label="Compliance exceptions" hint="Missing hours, missing offers, affordability failures, unresolved codes" checked={form.notifyExceptions} onChange={(v) => update("notifyExceptions", v)} />
            <div className="space-y-1">
              <Label><TermLabel term="deadlineFurnish">Reminder days before deadline</TermLabel></Label>
              <Input type="number" min={1} max={120} value={form.reminderDays} onChange={(e) => update("reminderDays", Number(e.target.value))} className="w-[140px]" />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Toggle({ label, hint, checked, onChange, term }: { label: string; hint: string; checked: boolean; onChange: (v: boolean) => void; term?: GlossaryKey }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div className="space-y-0.5">
        <Label>{term ? <TermLabel term={term}>{label}</TermLabel> : label}</Label>
        <p className="text-xs text-muted-foreground">{hint}</p>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}
